import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Dna, Download, Share2, TrendingUp, TrendingDown, 
  Clock, Server, Database, Zap, RefreshCw
} from 'lucide-react';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CostDNA = () => {
  const [dnaData, setDnaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const { shouldShowData } = useDemoData();

  useEffect(() => {
    generateCostDNA();
  }, []);

  useEffect(() => {
    if (dnaData && canvasRef.current) {
      drawDNAHelix();
    }
  }, [dnaData]);

  const generateCostDNA = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch cost data to generate DNA
      const [costsRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/by-service`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API_URL}/api/dashboard/trends?days=30`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);

      const costs = costsRes.ok ? await costsRes.json() : { breakdown: [] };
      const trends = trendsRes.ok ? await trendsRes.json() : { trends: [] };

      // Generate DNA traits based on spending patterns
      const traits = analyzeSpendingPatterns(costs.breakdown || [], trends.trends || []);
      setDnaData(traits);
    } catch (error) {
      console.error('Error generating Cost DNA:', error);
      if (shouldShowData) {
        setDnaData(getDemoTraits());
      }
    } finally {
      setLoading(false);
    }
  };

  const analyzeSpendingPatterns = (breakdown, trends) => {
    const totalCost = breakdown.reduce((sum, b) => sum + (b.cost || 0), 0);
    
    // Compute traits
    const computeRatio = breakdown.filter(b => 
      b.service?.toLowerCase().includes('ec2') || 
      b.service?.toLowerCase().includes('compute') ||
      b.service?.toLowerCase().includes('vm')
    ).reduce((sum, b) => sum + b.cost, 0) / (totalCost || 1);

    const storageRatio = breakdown.filter(b => 
      b.service?.toLowerCase().includes('s3') || 
      b.service?.toLowerCase().includes('storage') ||
      b.service?.toLowerCase().includes('blob')
    ).reduce((sum, b) => sum + b.cost, 0) / (totalCost || 1);

    const databaseRatio = breakdown.filter(b => 
      b.service?.toLowerCase().includes('rds') || 
      b.service?.toLowerCase().includes('database') ||
      b.service?.toLowerCase().includes('sql')
    ).reduce((sum, b) => sum + b.cost, 0) / (totalCost || 1);

    // Time patterns from trends
    const avgCost = trends.reduce((sum, t) => sum + (t.cost || 0), 0) / (trends.length || 1);
    const variance = trends.reduce((sum, t) => sum + Math.pow((t.cost || 0) - avgCost, 2), 0) / (trends.length || 1);
    const volatility = Math.sqrt(variance) / (avgCost || 1);

    // Growth trend
    const firstHalf = trends.slice(0, Math.floor(trends.length / 2));
    const secondHalf = trends.slice(Math.floor(trends.length / 2));
    const firstAvg = firstHalf.reduce((s, t) => s + (t.cost || 0), 0) / (firstHalf.length || 1);
    const secondAvg = secondHalf.reduce((s, t) => s + (t.cost || 0), 0) / (secondHalf.length || 1);
    const growthRate = ((secondAvg - firstAvg) / (firstAvg || 1)) * 100;

    return {
      // Core traits (0-100 scale)
      compute: Math.min(100, computeRatio * 200),
      storage: Math.min(100, storageRatio * 300),
      database: Math.min(100, databaseRatio * 300),
      volatility: Math.min(100, volatility * 200),
      growth: Math.min(100, Math.max(0, 50 + growthRate)),
      efficiency: Math.min(100, Math.max(0, 100 - volatility * 100)),
      
      // Personality
      personality: determinePersonality(computeRatio, storageRatio, volatility, growthRate),
      
      // Sequence for DNA visualization
      sequence: generateDNASequence(computeRatio, storageRatio, databaseRatio, volatility),
      
      // Benchmarks
      industryAvg: {
        compute: 45,
        storage: 25,
        database: 20,
        volatility: 30,
        growth: 50,
        efficiency: 70
      }
    };
  };

  const determinePersonality = (compute, storage, volatility, growth) => {
    if (compute > 0.5) {
      return volatility > 0.3 
        ? { type: 'Compute Sprinter', emoji: '🏃', desc: 'High compute, variable workloads' }
        : { type: 'Compute Steady', emoji: '🏋️', desc: 'Heavy compute, stable patterns' };
    }
    if (storage > 0.3) {
      return { type: 'Data Hoarder', emoji: '📦', desc: 'Storage-centric architecture' };
    }
    if (growth > 20) {
      return { type: 'Rapid Scaler', emoji: '🚀', desc: 'Fast-growing infrastructure' };
    }
    if (volatility > 0.4) {
      return { type: 'Event Driven', emoji: '⚡', desc: 'Highly variable, event-based' };
    }
    return { type: 'Balanced Builder', emoji: '⚖️', desc: 'Well-balanced resource mix' };
  };

  const generateDNASequence = (compute, storage, database, volatility) => {
    // Generate a unique sequence based on traits
    const bases = ['A', 'T', 'G', 'C'];
    const sequence = [];
    
    for (let i = 0; i < 50; i++) {
      const seed = (compute * 1000 + storage * 100 + database * 10 + i) % 4;
      sequence.push(bases[Math.floor(seed)]);
    }
    
    return sequence.join('');
  };

  const getDemoTraits = () => ({
    compute: 72,
    storage: 35,
    database: 48,
    volatility: 28,
    growth: 65,
    efficiency: 78,
    personality: { type: 'Compute Sprinter', emoji: '🏃', desc: 'High compute, variable workloads' },
    sequence: 'ATGCATGCATGCTAGCTAGCATGCATGCTAGCTAGCATGCATGCTAGCTAG',
    industryAvg: { compute: 45, storage: 25, database: 20, volatility: 30, growth: 50, efficiency: 70 }
  });

  const drawDNAHelix = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    // DNA colors based on traits
    const colors = {
      A: `rgba(16, 185, 129, ${0.5 + dnaData.compute / 200})`, // Emerald
      T: `rgba(59, 130, 246, ${0.5 + dnaData.storage / 200})`, // Blue
      G: `rgba(245, 158, 11, ${0.5 + dnaData.database / 200})`, // Amber
      C: `rgba(239, 68, 68, ${0.5 + dnaData.volatility / 200})` // Red
    };

    const sequence = dnaData.sequence;
    const numPoints = sequence.length;
    const amplitude = 40;
    const frequency = 0.15;

    // Draw double helix
    for (let i = 0; i < numPoints; i++) {
      const x = (i / numPoints) * width;
      const y1 = height / 2 + Math.sin(i * frequency) * amplitude;
      const y2 = height / 2 + Math.sin(i * frequency + Math.PI) * amplitude;

      const base = sequence[i];
      const color = colors[base] || colors.A;

      // Draw strand 1
      ctx.beginPath();
      ctx.arc(x, y1, 6, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      // Draw strand 2
      ctx.beginPath();
      ctx.arc(x, y2, 6, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      // Draw connecting line (base pair)
      if (i % 3 === 0) {
        ctx.beginPath();
        ctx.moveTo(x, y1);
        ctx.lineTo(x, y2);
        ctx.strokeStyle = `rgba(148, 163, 184, 0.3)`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    // Connect the helix strands
    ctx.beginPath();
    ctx.moveTo(0, height / 2 + Math.sin(0) * amplitude);
    for (let i = 1; i < numPoints; i++) {
      const x = (i / numPoints) * width;
      const y = height / 2 + Math.sin(i * frequency) * amplitude;
      ctx.lineTo(x, y);
    }
    ctx.strokeStyle = 'rgba(16, 185, 129, 0.3)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, height / 2 + Math.sin(Math.PI) * amplitude);
    for (let i = 1; i < numPoints; i++) {
      const x = (i / numPoints) * width;
      const y = height / 2 + Math.sin(i * frequency + Math.PI) * amplitude;
      ctx.lineTo(x, y);
    }
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
    ctx.lineWidth = 3;
    ctx.stroke();
  };

  const TraitBar = ({ label, value, benchmark, icon: Icon, color }) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className="font-medium">{label}</span>
        </div>
        <span className="text-slate-400">{value.toFixed(0)}%</span>
      </div>
      <div className="relative h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div 
          className={`absolute left-0 top-0 h-full rounded-full ${color.replace('text-', 'bg-')}`}
          style={{ width: `${value}%` }}
        />
        <div 
          className="absolute top-0 h-full w-0.5 bg-slate-400"
          style={{ left: `${benchmark}%` }}
          title={`Industry avg: ${benchmark}%`}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Dna className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Analyzing your Cost DNA...</p>
        </div>
      </div>
    );
  }

  if (!dnaData && !loading) {
    return (
      <div className="space-y-6" data-testid="cost-dna-page">
        <EmptyDataState title="Cost DNA" description="Connect your cloud providers to generate your unique cloud spending fingerprint." />
      </div>
    );
  }

  return (
    <div className="relative space-y-6 p-6 overflow-hidden" data-testid="cost-dna-page">
      {/* Brand atmosphere orbs (decorative, behind content) */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/25 blur-[120px]" />
        <div className="absolute top-20 right-1/4 w-[380px] h-[380px] rounded-full bg-cyan-200/25 blur-[120px]" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between relative">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3 leading-tight">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 bg-gradient-to-br from-emerald-500 via-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Dna className="h-6 w-6 text-white" />
            </div>
            <span className="text-emerald-600">
              Cost DNA
            </span>
          </h1>
          <p className="text-slate-600 mt-2 font-medium">
            Your <span className="text-slate-900 font-semibold">unique cloud spending fingerprint</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={generateCostDNA} className="border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50">
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate
          </Button>
          <Button variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <Button variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* DNA Visualization */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-emerald-50 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-3">
                <span className="text-4xl">{dnaData.personality.emoji}</span>
                <div>
                  <span className="text-xl">{dnaData.personality.type}</span>
                  <p className="text-sm font-normal text-slate-400 mt-0.5">
                    {dnaData.personality.desc}
                  </p>
                </div>
              </CardTitle>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">
              {dnaData.efficiency.toFixed(0)}% Efficient
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <canvas 
            ref={canvasRef} 
            width={800} 
            height={120}
            className="w-full h-auto rounded-lg bg-slate-50 dark:bg-slate-800/50"
          />
          <div className="flex justify-center gap-6 mt-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span>Compute</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span>Storage</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span>Database</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>Volatility</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Traits Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resource Traits</CardTitle>
            <CardDescription>Your spending distribution by resource type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <TraitBar 
              label="Compute Intensity" 
              value={dnaData.compute} 
              benchmark={dnaData.industryAvg.compute}
              icon={Server}
              color="text-emerald-500"
            />
            <TraitBar 
              label="Storage Usage" 
              value={dnaData.storage} 
              benchmark={dnaData.industryAvg.storage}
              icon={Database}
              color="text-emerald-500"
            />
            <TraitBar 
              label="Database Reliance" 
              value={dnaData.database} 
              benchmark={dnaData.industryAvg.database}
              icon={Database}
              color="text-amber-500"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Behavior Traits</CardTitle>
            <CardDescription>Your spending patterns and efficiency</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <TraitBar 
              label="Cost Volatility" 
              value={dnaData.volatility} 
              benchmark={dnaData.industryAvg.volatility}
              icon={Zap}
              color="text-red-500"
            />
            <TraitBar 
              label="Growth Rate" 
              value={dnaData.growth} 
              benchmark={dnaData.industryAvg.growth}
              icon={TrendingUp}
              color="text-emerald-500"
            />
            <TraitBar 
              label="Efficiency Score" 
              value={dnaData.efficiency} 
              benchmark={dnaData.industryAvg.efficiency}
              icon={Clock}
              color="text-emerald-500"
            />
          </CardContent>
        </Card>
      </div>

      {/* DNA Sequence */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Unique Sequence</CardTitle>
          <CardDescription>This sequence is unique to your spending patterns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="font-mono text-sm bg-slate-100 dark:bg-slate-800 p-4 rounded-lg overflow-x-auto">
            {dnaData.sequence.split('').map((base, i) => (
              <span 
                key={i} 
                className={
                  base === 'A' ? 'text-emerald-500' :
                  base === 'T' ? 'text-emerald-500' :
                  base === 'G' ? 'text-amber-500' :
                  'text-red-500'
                }
              >
                {base}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostDNA;
